import React, { useContext } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboardIcon, PackageIcon, UsersIcon, TruckIcon, WalletIcon, ClipboardPlusIcon, BarChart3Icon, SettingsIcon, ChevronLeftIcon, ChevronRightIcon, MenuIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageContext } from '../App';
import { buttonHover, buttonTap } from '../utils/animations';
import { Tooltip } from './ui/Tooltip';
interface SidebarProps {
  collapsed: boolean;
  toggleSidebar: () => void;
}
export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  toggleSidebar
}) => {
  const {
    language,
    direction
  } = useContext(LanguageContext);
  const location = useLocation();
  const menuItems = [{
    id: 'dashboard',
    path: '/dashboard',
    icon: <LayoutDashboardIcon size={20} data-id="element-677" />,
    label: language === 'en' ? 'Dashboard' : 'لوحة التحكم'
  }, {
    id: 'orders',
    path: '/orders',
    icon: <PackageIcon size={20} data-id="element-678" />,
    label: language === 'en' ? 'Orders' : 'الطلبات'
  }, {
    id: 'users',
    path: '/users',
    icon: <UsersIcon size={20} data-id="element-679" />,
    label: language === 'en' ? 'Users' : 'المستخدمين'
  }, {
    id: 'drivers',
    path: '/drivers',
    icon: <TruckIcon size={20} data-id="element-680" />,
    label: language === 'en' ? 'Drivers' : 'السائقين'
  }, {
    id: 'wallet',
    path: '/wallet',
    icon: <WalletIcon size={20} data-id="element-681" />,
    label: language === 'en' ? 'Wallet & Transactions' : 'المحفظة والمعاملات'
  }, {
    id: 'manual-booking',
    path: '/manual-booking',
    icon: <ClipboardPlusIcon size={20} data-id="element-682" />,
    label: language === 'en' ? 'Manual Booking' : 'الحجز اليدوي'
  }, {
    id: 'reports',
    path: '/reports',
    icon: <BarChart3Icon size={20} data-id="element-683" />,
    label: language === 'en' ? 'Reports & Analytics' : 'التقارير والتحليلات'
  }, {
    id: 'settings',
    path: '/settings',
    icon: <SettingsIcon size={20} data-id="element-684" />,
    label: language === 'en' ? 'Settings' : 'الإعدادات'
  }];
  // Determine which chevron to use based on language direction and collapsed state
  const getToggleIcon = () => {
    if (direction === 'rtl') {
      return collapsed ? <ChevronLeftIcon size={20} data-id="element-685" /> : <ChevronRightIcon size={20} data-id="element-686" />;
    } else {
      return collapsed ? <ChevronRightIcon size={20} data-id="element-687" /> : <ChevronLeftIcon size={20} data-id="element-688" />;
    }
  };
  const sidebarVariants = {
    expanded: {
      width: '16rem'
    },
    collapsed: {
      width: '5rem'
    }
  };
  return <motion.div className={`bg-white border-r border-gray-200 h-full flex flex-col transition-all duration-300 ease-in-out`} initial={collapsed ? 'collapsed' : 'expanded'} animate={collapsed ? 'collapsed' : 'expanded'} variants={sidebarVariants} data-id="element-689">
      <div className={`p-4 border-b border-gray-200 ${collapsed ? 'flex justify-center' : ''}`} data-id="element-690">
        {collapsed ? <motion.div className="w-10 h-10 rounded-md bg-congress-blue-600 flex items-center justify-center text-white font-bold" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-691">
            R
          </motion.div> : <div className="flex items-center" data-id="element-692">
            <motion.div className="w-10 h-10 rounded-md bg-congress-blue-600 flex items-center justify-center text-white font-bold" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} data-id="element-693">
              R
            </motion.div>
            <motion.span className="ml-2 text-xl font-bold text-gray-800" initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-694">
              RIDERS
            </motion.span>
          </div>}
      </div>
      <nav className="flex-1 overflow-y-auto p-2" data-id="element-695">
        <ul className={`space-y-2 ${collapsed ? 'px-2' : 'px-2'}`} data-id="element-696">
          {menuItems.map(item => <li key={item.id} data-id="element-697">
              {collapsed ? <Tooltip content={item.label} side={direction === 'rtl' ? 'left' : 'right'} data-id="element-698">
                  <NavLink to={item.path} className={({
              isActive
            }) => `flex items-center px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-congress-blue-50 text-congress-blue-700' : 'text-gray-700 hover:bg-gray-100'} ${collapsed ? 'justify-center' : ''}`} data-id="element-699">
                    {({
                isActive
              }) => <>
                        <motion.span whileHover={{
                  scale: 1.1
                }} whileTap={{
                  scale: 0.9
                }} data-id="element-700">
                          {item.icon}
                        </motion.span>
                        {isActive && !collapsed && <motion.div className="absolute left-0 w-1 h-6 bg-congress-blue-600 rounded-r-full" layoutId="activeIndicator" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.2
                }} data-id="element-701" />}
                      </>}
                  </NavLink>
                </Tooltip> : <NavLink to={item.path} className={({
            isActive
          }) => `flex items-center px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-congress-blue-50 text-congress-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-702">
                  {({
              isActive
            }) => <>
                      <motion.span className="mr-3" whileHover={{
                scale: 1.1
              }} whileTap={{
                scale: 0.9
              }} data-id="element-703">
                        {item.icon}
                      </motion.span>
                      <AnimatePresence data-id="element-704">
                        <motion.span initial={{
                  opacity: 0,
                  width: 0
                }} animate={{
                  opacity: 1,
                  width: 'auto'
                }} exit={{
                  opacity: 0,
                  width: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-705">
                          {item.label}
                        </motion.span>
                      </AnimatePresence>
                      {isActive && <motion.div className="absolute left-0 w-1 h-6 bg-congress-blue-600 rounded-r-full" layoutId="activeIndicator" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.2
              }} data-id="element-706" />}
                    </>}
                </NavLink>}
            </li>)}
        </ul>
      </nav>
      <div className="p-4 border-t border-gray-200" data-id="element-707">
        <Tooltip content={collapsed ? language === 'en' ? 'Expand Sidebar' : 'توسيع الشريط الجانبي' : language === 'en' ? 'Collapse Sidebar' : 'طي الشريط الجانبي'} side="top" data-id="element-708">
          <motion.button onClick={toggleSidebar} className="w-full flex items-center justify-center p-2 rounded-md text-gray-500 hover:bg-gray-100 transition-colors" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} whileHover={buttonHover} whileTap={buttonTap} data-id="element-709">
            {getToggleIcon()}
          </motion.button>
        </Tooltip>
      </div>
    </motion.div>;
};